import React, { useState } from "react";
import { toast } from "sonner";
import { FilePdf } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function canGetConsentProof(user, hasPerm) {
  if (!user) return false;
  if (user.org_type === "internal") return hasPerm("export_leads");
  return user.role === "buyer" || user.org_type === "client";
}

async function errText(err) {
  try { return JSON.parse(await err.response.data.text()).detail; } catch { return null; }
}

export { errText as blobError };

export function saveBlob(resp, fallback) {
  const m = (resp.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/);
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = m ? m[1] : fallback;
  a.click();
  URL.revokeObjectURL(url);
}

// Downloads one lead's proof of consent (IP, time, wording, TrustedForm link) as a PDF.
export function ConsentProofButton({ leadId, compact = false }) {
  const { user, hasPerm } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!canGetConsentProof(user, hasPerm)) return null;
  const run = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const resp = await api.get(`/leads/${leadId}/consent-proof`, { responseType: "blob" });
      saveBlob(resp, `consent-proof-${leadId.slice(0, 8)}.pdf`);
      toast.success("Consent proof downloaded");
    } catch (err) {
      toast.error((await errText(err)) || "Couldn't build the consent proof");
    } finally { setBusy(false); }
  };
  if (compact) {
    return (
      <button type="button" onClick={run} disabled={busy} title="Download consent proof (PDF)"
        aria-label="Download consent proof PDF" data-testid={`consent-proof-btn-${leadId}`}
        className="inline-flex items-center gap-1 border border-black px-1.5 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white disabled:opacity-40">
        <FilePdf size={13} /> {busy ? "…" : "PDF"}
      </button>
    );
  }
  return (
    <button type="button" onClick={run} disabled={busy} data-testid="consent-proof-btn"
      title="One-page PDF with visitor IP, time, consent wording and TrustedForm link"
      className="border-2 border-sky-800 bg-sky-50 text-sky-900 px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-1 transition-colors duration-100 hover:bg-sky-800 hover:text-white disabled:opacity-40">
      <FilePdf size={12} weight="bold" /> {busy ? "Building…" : "Consent proof PDF"}
    </button>
  );
}
